import { useCallback, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { tenantsService, type TenantView } from '../../../services/tenants/tenants.service';
import { useAdminTenants } from '../../../contexts/AdminEventsProvider';

type UseHeaderEditControllerParams = {
  tenantId: string;
  onError: (message: string) => void;
};

// Summary: Super-admin header-edit modal state + diff-only PATCH; maps tenantId to tenant PK.
export function useHeaderEditController({ tenantId, onError }: UseHeaderEditControllerParams) {
  const { tenants } = useAdminTenants();
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [tenant, setTenant] = useState<TenantView | null>(null);

  // Resolves tenantId string to a TenantView (PK); falls back to list() if the snapshot is empty.
  const open = useCallback(async () => {
    const fromSnapshot = tenants.find((candidate) => candidate.tenantId === tenantId) ?? null;
    if (fromSnapshot) {
      setTenant(fromSnapshot);
      setIsOpen(true);
      return;
    }
    try {
      const list = await tenantsService.list();
      const resolved = list.find((candidate) => candidate.tenantId === tenantId) ?? null;
      if (!resolved) {
        onError('עדכון הכותרת נכשל');
        return;
      }
      setTenant(resolved);
      setIsOpen(true);
    } catch (loadError: unknown) {
      onError(loadError instanceof Error ? loadError.message : 'עדכון הכותרת נכשל');
    }
  }, [tenants, tenantId, onError]);

  const close = useCallback(() => setIsOpen(false), []);

  // Diff-only submit (Rule 19): unchanged header closes, no request; else PATCH by tenant PK.
  const submit = useCallback(
    async (target: TenantView, header: string) => {
      if (header === (target.header ?? '')) {
        setIsOpen(false);
        return;
      }
      setIsSaving(true);
      try {
        await tenantsService.updateHeader(target.id, header);
        setIsOpen(false);
        // No broadcast: docs fetch the header fresh on open (in-order refresh deferred anyway).
        notifications.show({ color: 'green', message: 'הכותרת עודכנה בהצלחה', autoClose: 3000 });
      } catch (saveError: unknown) {
        onError(saveError instanceof Error ? saveError.message : 'עדכון הכותרת נכשל');
      } finally {
        setIsSaving(false);
      }
    },
    [onError],
  );

  return { isOpen, isSaving, tenant, open, close, submit };
}
